import { DepthTexture, FloatType, NearestFilter, RGBAFormat, SRGBColorSpace, UnsignedByteType, WebGLMultipleRenderTargets, WebGLRenderTarget, } from "three";
/**
 * Encapsulates the full set of all render targets and their buffers. This will create 3 'RenderTargetSet' sets and will
 * encapsulate cycling through this set of buffers between frames.
 */
export class RenderTargets {
    /**
     * Constructs a new 'RenderTargets' object, including creating the full set of 'RenderTargetSet' objects.
     *
     * This will also register
     * @param threeRenderer
     * @param width
     * @param height
     */
    constructor(threeRenderer, width, height) {
        this._targets = [];
        this._current = 0;
        this._resizeCallback = null;
        this._threeRenderer = threeRenderer;
        for (let i = 0; i < 2; i += 1) {
            this._targets.push(RenderTargets.createRenderTargetAndPassSet(width, height));
        }
        this.registerResizeListeners();
    }
    /**
     * Unregister all resize listeners and dispose all resources owned by the 'RenderTargets' instance.
     */
    dispose() {
        this.unregisterResizeListener();
        for (const t of this._targets) {
            t.main.texture.forEach((v) => v.dispose());
            t.main.dispose();
            t.kml.texture.dispose();
            t.kml.dispose();
            t.depthReadBack.texture.dispose();
            t.depthReadBack.dispose();
            t.noPicking.texture.dispose();
            t.noPicking.dispose();
        }
    }
    /**
     * Returns a read-only view of the full set of all render targets.
     */
    get targets() {
        return this._targets;
    }
    /**
     * Returns the current target set index.
     */
    get currentIndex() {
        return this._current;
    }
    /**
     * Returns the target set index that should be used for picking read-back.
     */
    get pickIndex() {
        return (this._current + 1) % this._targets.length;
    }
    /**
     * Returns the current target set.
     */
    get current() {
        return this._targets[this._current];
    }
    /**
     * Increments (and wraps, if required) the target set index to the next set.
     */
    incrementBufferIndex() {
        this._current = (this._current + 1) % this._targets.length;
    }
    /**
     * Internal function for adding the resize listener to the renderer
     * @private
     */
    registerResizeListeners() {
        if (this._resizeCallback !== null) {
            throw new Error("Trying to register a 'RenderTargets' resize handler multiple times");
        }
        this._resizeCallback = (_three, _webgl, newWidth, newHeight) => {
            for (const t of this._targets) {
                t.main.setSize(newWidth, newHeight);
                t.noPicking.setSize(newWidth, newHeight);
                t.kml.setSize(newWidth, newHeight);
                t.depthReadBack.setSize(newWidth, newHeight);
            }
        };
        this._threeRenderer.addResizeListener(this._resizeCallback);
    }
    /**
     * Internal function for removing the resize listener from the renderer
     * @private
     */
    unregisterResizeListener() {
        if (this._resizeCallback !== null) {
            this._threeRenderer.removeResizeListener(this._resizeCallback);
        }
    }
    /**
     * Construct one target set of the provided width/height.
     * @param width
     * @param height
     * @private
     */
    static createRenderTargetAndPassSet(width, height) {
        // Construct the depth read back texture and add a resize handler to the renderer
        const depthReadBackBuffer = new WebGLRenderTarget(width, height, {
            format: RGBAFormat,
            type: FloatType,
            generateMipmaps: false,
        });
        // Construct the main render target texture and add a resize handler to the renderer
        const mainRenderTarget = new WebGLMultipleRenderTargets(width, height, 3);
        mainRenderTarget.texture[0].minFilter = NearestFilter;
        mainRenderTarget.texture[0].magFilter = NearestFilter;
        mainRenderTarget.texture[0].format = RGBAFormat;
        mainRenderTarget.texture[0].type = UnsignedByteType;
        // @ts-ignore
        mainRenderTarget.texture[0].colorSpace = SRGBColorSpace;
        mainRenderTarget.texture[0].generateMipmaps = false;
        mainRenderTarget.texture[0].name = "pc_fragColor";
        mainRenderTarget.texture[1].minFilter = NearestFilter;
        mainRenderTarget.texture[1].magFilter = NearestFilter;
        mainRenderTarget.texture[1].format = RGBAFormat;
        mainRenderTarget.texture[1].type = UnsignedByteType;
        mainRenderTarget.texture[1].generateMipmaps = false;
        mainRenderTarget.texture[1].name = "pc_pickingBuffer1";
        mainRenderTarget.texture[2].minFilter = NearestFilter;
        mainRenderTarget.texture[2].magFilter = NearestFilter;
        mainRenderTarget.texture[2].format = RGBAFormat;
        mainRenderTarget.texture[2].type = UnsignedByteType;
        mainRenderTarget.texture[2].generateMipmaps = false;
        mainRenderTarget.texture[2].name = "pc_pickingBuffer2";
        // Typescript definitions are missing for this field because of course they are so I tell the ts compiler to shut up
        // @ts-ignore
        mainRenderTarget.depthBuffer = true;
        // @ts-ignore
        mainRenderTarget.depthTexture = new DepthTexture(width, height);
        // This is a disgusting hack to trick Three.JS into allowing readRenderTargetPixels to work for an MRT framebuffer.
        // None of these fields actually exist on WebGLMultipleRenderTargets, but we once again must shut the ts compiler up
        //
        // I am starting to like Three.js less :(
        // @ts-ignore
        mainRenderTarget.texture.format = RGBAFormat;
        // @ts-ignore
        mainRenderTarget.texture.type = UnsignedByteType;
        const noPickingMainRenderTarget = new WebGLRenderTarget(width, height, {
            // @ts-ignore
            depthTexture: mainRenderTarget.depthTexture,
            depthBuffer: true,
            minFilter: NearestFilter,
            magFilter: NearestFilter,
            format: RGBAFormat,
            type: UnsignedByteType,
            generateMipmaps: false,
        });
        noPickingMainRenderTarget.texture = mainRenderTarget.texture[0];
        const kmlMainRenderTarget = new WebGLRenderTarget(width, height, {
            depthBuffer: true,
            minFilter: NearestFilter,
            magFilter: NearestFilter,
            format: RGBAFormat,
            type: UnsignedByteType,
            generateMipmaps: false,
        });
        kmlMainRenderTarget.texture = mainRenderTarget.texture[0];
        return {
            main: mainRenderTarget,
            depthReadBack: depthReadBackBuffer,
            kml: kmlMainRenderTarget,
            noPicking: noPickingMainRenderTarget,
        };
    }
}
